import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ChartBar, FileXls } from '@phosphor-icons/react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtDM = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}` : '');
const fmtDMY = (iso) => (iso ? `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(0, 4)}` : '');

export function DaywiseProductivity({ productionUser }) {
  const [dateFrom, setDateFrom] = useState(monthStart());
  const [dateTo, setDateTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (df, dt) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: df, date_to: dt });
      if (productionUser && productionUser !== 'all') params.set('production_user', productionUser);
      const { data: res } = await api.get(`/api/production/productivity-daywise?${params}`);
      setData(res);
    } catch (e) {
      if (!e?.isAuthError) toast.error('Failed to load day-wise productivity');
    } finally {
      setLoading(false);
    }
  }, [productionUser]);

  useEffect(() => { load(dateFrom, dateTo); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const headers = ['Date', 'JOs Completed', 'Windows (Nos)', 'Sqft'];
    const rows = data.rows.map((r) => [fmtDMY(r.date), r.jobs, r.windows, r.sqft]);
    rows.push(['TOTAL', data.totals.total_jobs, data.totals.total_windows, data.totals.total_sqft]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `daywise_productivity_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const chartData = (data?.rows || []).map((r) => ({ ...r, day: fmtDM(r.date) }));

  return (
    <Card className="border-zinc-300 rounded-sm shadow-sm" data-testid="daywise-productivity-card">
      <CardHeader className="border-b border-zinc-300 py-3 px-6 flex flex-row flex-wrap items-end justify-between gap-3">
        <CardTitle className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
          <ChartBar size={16} weight="bold" /> Day-wise Production Output
        </CardTitle>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">From</Label>
            <Input data-testid="daywise-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">To</Label>
            <Input data-testid="daywise-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" />
          </div>
          <Button data-testid="daywise-search" size="sm" className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white" onClick={() => load(dateFrom, dateTo)}>Apply</Button>
          <Button data-testid="daywise-export" size="sm" variant="outline" className="h-9" onClick={exportCsv} disabled={!data?.rows?.length}>
            <FileXls size={14} className="mr-1" /> CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[['Working Days', data.totals.total_days], ['JOs Completed', data.totals.total_jobs],
              ['Windows (Nos)', data.totals.total_windows], ['Total Sqft', data.totals.total_sqft]].map(([k, v]) => (
              <div key={k} className="p-3 rounded-sm border border-zinc-200 bg-zinc-50">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{k}</div>
                <div className="text-xl font-bold text-zinc-950" data-testid={`daywise-total-${k.split(' ')[0].toLowerCase()}`}>{v}</div>
              </div>
            ))}
          </div>
        )}

        {loading ? (
          <div className="py-10 text-center text-zinc-400">Loading…</div>
        ) : !data?.rows?.length ? (
          <div className="py-10 text-center text-zinc-400" data-testid="daywise-empty">No production completed in this date range</div>
        ) : (
          <>
            <div className="h-72 w-full" data-testid="daywise-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} label={{ value: 'Windows (Nos)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} label={{ value: 'Sqft', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip formatter={(v, name) => [v, name]} labelFormatter={(l) => `Date: ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="windows" name="Windows (Nos)" fill="#f97316" radius={[3, 3, 0, 0]} maxBarSize={34} />
                  <Line yAxisId="right" type="monotone" dataKey="sqft" name="Sqft" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="daywise-table">
                <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="p-2 text-left">Date</th>
                    <th className="p-2 text-right">JOs Completed</th>
                    <th className="p-2 text-right">Windows (Nos)</th>
                    <th className="p-2 text-right">Sqft</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.date} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`daywise-row-${r.date}`}>
                      <td className="p-2 font-mono">{fmtDMY(r.date)}</td>
                      <td className="p-2 text-right">{r.jobs}</td>
                      <td className="p-2 text-right font-bold text-orange-700">{r.windows}</td>
                      <td className="p-2 text-right font-bold text-blue-700">{r.sqft}</td>
                    </tr>
                  ))}
                  <tr className="bg-zinc-50 font-bold">
                    <td className="p-2 uppercase text-xs">Total</td>
                    <td className="p-2 text-right">{data.totals.total_jobs}</td>
                    <td className="p-2 text-right text-orange-700">{data.totals.total_windows}</td>
                    <td className="p-2 text-right text-blue-700">{data.totals.total_sqft}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
